import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography
} from '@mui/material';
import { fromUnixTime } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { isEmpty } from 'lodash';

import ConfirmationDialog from './ConfirmationDialog';
import { Subscription } from './stripe/types';
import { Colors } from '../utils/Colors';

interface SubscriptionDialogProps {
  close: () => void;
  onCancel: () => void;
  open: boolean;
  subscription: Subscription;
  subscriptionStatus: 'initial' | 'new' | 'updated' | 'display' | 'cancelled';
}

export const SubscriptionDialog = ({
  close,
  onCancel,
  open,
  subscription,
  subscriptionStatus
}: SubscriptionDialogProps) => {
  const isNew = useMemo(() => isEmpty(subscription), []);
  const [bannerOpen, setBannerOpen] = useState(
    subscriptionStatus !== 'initial' && subscriptionStatus !== 'display'
  );
  const [confirmation, setConfirmation] = useState(false);

  const noSub =
    subscription.status !== 'active' &&
    (!subscription.ended_at ||
      !subscription.current_period_end ||
      subscription.current_period_end * 1000 < Date.now());

  useEffect(
    () =>
      setBannerOpen(
        subscriptionStatus !== 'initial' && subscriptionStatus !== 'display'
      ),
    [subscriptionStatus]
  );

  const openConfirmation = () => {
    setConfirmation(true);
  };

  const closeConfirmation = () => {
    setConfirmation(false);
  };

  const onConfirmation = () => {
    onCancel();
    closeConfirmation();
  };

  return (
    <>
      <Dialog sx={{}} open={open}>
        <Paper elevation={3}>
          <DialogTitle
            sx={{ backgroundColor: Colors.celeste, padding: '1.5rem' }}
          >
            <Typography sx={{ marginRight: '2rem' }} variant="h5">
              Your Subscription
            </Typography>
            <IconButton
              sx={{
                position: 'absolute',
                right: 8,
                top: 8
              }}
              onClick={close}
            >
              <Close />
            </IconButton>
          </DialogTitle>
        </Paper>
        {bannerOpen ? (
          <Paper
            sx={{
              alignItems: 'center',
              backgroundColor: Colors.ocdGreen,
              borderRadius: '0px',
              padding: '0.25rem',
              paddingLeft: '1.5rem',
              paddingRight: '0.5rem',
              display: 'flex',
              justifyContent: 'space-between',
              position: 'relative',
              width: '100%'
            }}
            elevation={3}
          >
            <Typography variant="body2">
              Subscription{' '}
              {subscriptionStatus === 'new' ? 'Created' : 'Updated'}
            </Typography>
            <IconButton onClick={() => setBannerOpen(false)}>
              <Close />
            </IconButton>
          </Paper>
        ) : null}
        <DialogContent
          sx={{
            flexDirection: 'column',
            padding: '1.5rem',
            paddingTop: '2rem'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontWeight: 800 }} variant="body1">
              Status
            </Typography>
            <Typography variant="body1">
              {subscription?.status
                ? `${subscription.status
                    .slice(0, 1)
                    .toUpperCase()}${subscription.status.slice(1)}`
                : 'Not Started'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontWeight: 800 }} variant="body1">
              Started
            </Typography>
            <Typography variant="body1">
              {subscription?.created
                ? fromUnixTime(subscription.created).toLocaleDateString()
                : 'Not Started'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontWeight: 800 }} variant="body1">
              Period Start Date
            </Typography>
            <Typography variant="body1">
              {subscription?.current_period_start
                ? fromUnixTime(
                    subscription.current_period_start
                  ).toLocaleDateString()
                : 'Not Started'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontWeight: 800 }} variant="body1">
              Period End Date
            </Typography>
            <Typography variant="body1">
              {subscription?.current_period_end
                ? fromUnixTime(
                    subscription.current_period_end
                  ).toLocaleDateString()
                : 'Not Started'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography
              sx={{ fontWeight: 800, marginRight: '2rem' }}
              variant="body1"
            >
              Subscription Type
            </Typography>
            <Typography variant="body1">
              {subscription?.plan
                ? `$${subscription.plan.amount_decimal.slice(0, -2)}.00 per ${
                    subscription.plan.interval
                  }`
                : 'Not Started'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={isNew ? { display: 'block' } : {}}>
          {!noSub && !isNew ? (
            <Link to="/dashboard">
              <Button
                disableElevation
                variant="contained"
                style={{
                  backgroundColor: Colors.lightGray,
                  color: 'black'
                }}
              >
                To Dashboard
              </Button>
            </Link>
          ) : null}
          {!noSub && isNew ? (
            <Link to="/profile">
              <Button
                disableElevation
                variant="contained"
                style={{
                  backgroundColor: Colors.lightGray,
                  color: 'black',
                  width: '100%'
                }}
              >
                To Profile
              </Button>
            </Link>
          ) : null}
          {subscription?.status === 'active' && !isNew ? (
            <Button
              onClick={openConfirmation}
              color="error"
              disableElevation
              variant="contained"
              sx={{
                color: 'black',
                marginLeft: '1rem'
              }}
            >
              Cancel Subscription
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        onClose={closeConfirmation}
        open={confirmation}
        onSubmit={onConfirmation}
      />
    </>
  );
};
